import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import InputCard from "../../../../Components/Cards/InputCard";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import {
  submitTransactionAdjustment,
  getVendorAdjustableTransactions,
  getFuelBrands,
} from "../../../../API/FuelVendor/TransactionAdjustments";
import "./style.css";
import usePageAccess from "../../../../Utlis/usePageAccess";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data.message === "string") return data.message;
  if (typeof data.detail === "string") return data.detail;

  const messages = Object.entries(data).flatMap(([field, errors]) => {
    const list = Array.isArray(errors) ? errors : [errors];
    return list.map((err) => `${field.replace(/_/g, " ")}: ${err}`);
  });

  return messages.length ? messages.join(" | ") : fallback;
};

const RequestAdjustments = () => {
  const { canEdit } = usePageAccess("transactions");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [documents, setDocuments] = useState([]);
  const [adjustableTransactions, setAdjustableTransactions] = useState([]);
  const [fuelBrands, setFuelBrands] = useState([]);

  useEffect(() => {
    const fetchAdjustableTransactions = async () => {
      try {
        const res = await getVendorAdjustableTransactions();

        if (Math.floor(res?.status / 100) === 2) {
          setAdjustableTransactions(res?.data?.data || []);
        }
      } catch (err) {
        console.error("Failed to load adjustable transactions:", err);
      }
    };

    fetchAdjustableTransactions();
  }, []);

  useEffect(() => {
    const fetchFuelBrands = async () => {
      try {
        const res = await getFuelBrands();

        if (Math.floor(res?.status / 100) === 2) {
          setFuelBrands(res?.data?.data || []);
        }
      } catch (err) {
        console.error("Failed to load fuel brands:", err);
      }
    };

    fetchFuelBrands();
  }, []);

  const handleValuesChange = (changedValues) => {
    if (!("id" in changedValues)) return;

    const selectedTxn = adjustableTransactions.find(
      (txn) => txn.id === changedValues.id,
    );

    form.setFieldsValue({
      originalAmount: selectedTxn ? parseFloat(selectedTxn.amount) : undefined,
      fuelType: selectedTxn?.fuel_type_name || undefined,
    });
  };

  const originalAmount = Form.useWatch("originalAmount", form);
  const newAmount = Form.useWatch("newAmount", form);

  const hasBothAmounts =
    originalAmount !== undefined &&
    originalAmount !== "" &&
    newAmount !== undefined &&
    newAmount !== "";

  const netAmount = hasBothAmounts
    ? (parseFloat(newAmount) || 0) - (parseFloat(originalAmount) || 0)
    : 0;
  const isPositive = netAmount > 0;
  const isZero = netAmount === 0;

  const displayAmount = isPositive
    ? `+$${netAmount.toFixed(2)}`
    : `$${netAmount.toFixed(2)}`;
  const badgeText = isZero
    ? "NO CHANGE"
    : isPositive
      ? "INCREASED"
      : "DECREASED";

  const styleModifier = isZero
    ? "neutral"
    : isPositive
      ? "positive"
      : "negative";

  const initialValues = {};

  const handleBack = () => {
    navigate(-1);
  };

  const transactionInformation = [
    {
      name: "id",
      label: "Transaction ID / Reference Number",
      fullWidth: true,
      type: "select",
      placeholder: "Select a transaction",
      options: adjustableTransactions.map((txn) => ({
        label: `${txn.txn_id} — ${txn.vehicle_name || "-"} (${txn.license_plate || "-"})${
          txn.fuel_type_name ? ` · ${txn.fuel_type_name}` : ""
        }`,
        value: txn.id,
      })),
      rules: [{ required: true, message: "Transaction is required" }],
    },
  ];

  const adjustmentDetails = [
    {
      name: "vendor",
      label: "Select Vendor",
      type: "select",
      placeholder: "Select Vendor",
      options: fuelBrands.map((brand) => ({
        label: brand.name,
        value: brand.id,
      })),
      className: "vendor-select-field",
      rules: [{ required: true, message: "Vendor is required" }],
    },
    {
      name: "adjustmentType",
      label: " ",
      type: "standard-radio",
      options: [
        { label: "Refund", value: "refund" },
        { label: "Correction", value: "correction" },
        { label: "Fee Edit", value: "fee_edit" },
      ],
      rules: [{ required: true, message: "Adjustment type is required" }],
    },
    {
      name: "fuelType",
      label: "Fuel Type",
      type: "input",
      placeholder: "Select a transaction first",
      disabled: true,
    },
    {
      name: "originalAmount",
      label: "Original Amount",
      type: "currency-input",
      placeholder: "Select a transaction first",
      disabled: true,
      rules: [{ required: true, message: "Original amount is required" }],
    },
    {
      name: "newAmount",
      label: "New Amount",
      type: "currency-input",
      placeholder: "Enter new amount",
      rules: [{ required: true, message: "New amount is required" }],
    },
    {
      name: "reason",
      label: "Reason for Adjustment",
      type: "textarea",
      placeholder: "Reason",
      fullWidth: true,
      rows: 5,
      customStyle: { minHeight: 100, paddingTop: 8, paddingBottom: 8 },
      rules: [{ required: true, message: "Reason is required" }],
    },
  ];

  const handleCancel = () => {
    form.resetFields();
    setDocuments([]);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const values = await form.validateFields();

      const formData = new FormData();
      formData.append("transaction_id", parseInt(values.id, 10));
      formData.append("new_amount", values.newAmount);
      formData.append("type", values.adjustmentType);
      formData.append("reason", values.reason);

      formData.append(
        "document_categories",
        JSON.stringify(documents.map((doc) => doc.category)),
      );
      formData.append(
        "document_expiry_dates",
        JSON.stringify(documents.map((doc) => doc.expiryDate || null)),
      );
      documents.forEach((doc) => {
        formData.append("documents", doc.file);
      });

      const res = await submitTransactionAdjustment(formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          res?.data?.message || "Adjustment request submitted successfully!",
        );
        navigate("/transactions-vendor", {
          state: { activeTab: "Transaction Adjustments" },
        });

        form.resetFields();
        setDocuments([]);
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to submit adjustment request."),
        );
      }
    } catch (error) {
      if (error?.errorFields) {
        message.error("Please complete all required fields before submitting.");
      } else {
        message.error(
          getErrorMessage(
            error?.response?.data,
            "Failed to submit adjustment request.",
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="request-adj">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
      >
        <div>
          <div className="transaction-heading">Request Adjustment</div>
          <InputCard title="Transaction ID" fields={transactionInformation} />
        </div>

        <div>
          <InputCard title="Adjustment Detail" fields={adjustmentDetails}>
            <div className="net-adjustment-container">
              <div className="net-adjustment-info">
                <span className="net-adjustment-label">Net Adjustment</span>

                <span className={`net-adjustment-amount ${styleModifier}`}>
                  {displayAmount}
                </span>
              </div>

              <span className={`net-adjustment-badge ${styleModifier}`}>
                {badgeText}
              </span>
            </div>
          </InputCard>
        </div>

        <div>
          <GenericDocumentManagerTable
            initialDocuments={documents}
            setDocuments={setDocuments}
            documentTypes={["Transaction Receipt"]}
          />
        </div>

        {canEdit && (
          <div className="btns-row">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button onClick={handleSubmit} loading={loading} className="save-btn">
              Submit For Approval
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default RequestAdjustments;
